import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import rehypePrism from 'rehype-prism-plus';
import Navigation from './Navigation';
import Footer from './Footer';
import api from '../utils/api';
import AppLoadingState, { LoadingSpinnerRing } from './common/AppLoadingState';

// Custom components for markdown rendering
const MarkdownComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="text-4xl font-bold text-white mb-8 mt-12 scroll-mt-24 lg:scroll-mt-24 scroll-mt-16" {...props} />
  ),
  h2: ({ node, ...props }) => {
    const id = props.children
      .toString()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+$/, '');
    return (
      <h2 id={id} className="text-3xl font-bold text-white mb-6 mt-12 scroll-mt-24 lg:scroll-mt-24 scroll-mt-16" {...props} />
    );
  },
  h3: ({ node, ...props }) => {
    const id = props.children
      .toString()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+$/, '');
    return (
      <h3 id={id} className="text-2xl font-semibold text-white mb-4 mt-8 scroll-mt-24 lg:scroll-mt-24 scroll-mt-16" {...props} />
    );
  },
  p: ({ node, ...props }) => (
    <p className="text-gray-300 text-lg leading-relaxed mb-6" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc list-inside mb-6" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal list-inside mb-6" {...props} />
  ),
  li: ({ node, ...props }) => (
    <li className="text-gray-300 mb-2" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a className="text-blue-400 hover:text-blue-300 underline" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-gray-600 pl-4 my-4 text-gray-400 italic" {...props} />
  ),
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="bg-gray-800 px-1 py-0.5 rounded text-sm font-mono" {...props} />
    ) : (
      <code className="block bg-gray-800 p-4 rounded-lg text-sm font-mono overflow-x-auto" {...props} />
    ),
  pre: ({ node, ...props }) => (
    <pre className="bg-gray-800 p-4 rounded-lg overflow-x-auto mb-6" {...props} />
  ),
  img: ({ node, ...props }) => (
    <img className="max-w-full h-auto rounded-lg my-6" {...props} alt={props.alt || ''} />
  ),
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto mb-6">
      <table className="min-w-full divide-y divide-gray-700" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th className="px-6 py-3 bg-gray-800 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300" {...props} />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-8 border-gray-700" {...props} />
  )
};

const ShareButton = ({ platform, icon, shareUrl, title }) => {
  const handleShare = () => {
    let url;
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <button
      onClick={handleShare}
      className="p-3 bg-white/10 rounded-full text-gray-300 hover:text-white transition-colors"
      aria-label={`Share on ${platform}`}
    >
      {icon}
    </button>
  );
};

const TableOfContents = ({ content, isMobile = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const headings = content.match(/#{2,3}\s+(.+)/g) || [];
  const toc = headings.map(heading => ({
    level: heading.match(/#{2,3}/)[0].length,
    text: heading.replace(/#{2,3}\s+/, ''),
    id: heading
      .replace(/#{2,3}\s+/, '')
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+$/, '')
  }));

  if (toc.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    if (isMobile) {
      setIsExpanded(false);
      // Wait for the mobile menu to collapse before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          window.history.pushState(null, '', `#${id}`);
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300); // Match the transition duration
    } else {
      const element = document.getElementById(id);
      if (element) {
        window.history.pushState(null, '', `#${id}`);
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const mobileClasses = `
    lg:hidden bg-white/5 backdrop-blur-xl rounded-2xl p-6 mb-8
    ${isExpanded ? 'max-h-[500px]' : 'max-h-[60px]'} 
    transition-all duration-300 ease-in-out overflow-hidden
  `;

  const desktopClasses = "hidden lg:block bg-white/5 backdrop-blur-xl rounded-2xl p-6 mb-8 sticky top-24";

  if (isMobile) {
    return (
      <div className={mobileClasses}>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-lg font-semibold text-white mb-4"
        >
          <span>Table of Contents</span>
          <svg 
            className={`w-5 h-5 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <nav className={`${isExpanded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
          <ul className="space-y-2">
            {toc.map((item, index) => (
              <li key={index} className={`${item.level === 2 ? 'ml-0' : 'ml-4'}`}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleClick(e, item.id)}
                  className="text-gray-400 hover:text-white transition-colors text-sm block"
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div className={desktopClasses}>
      <h3 className="text-lg font-semibold text-white mb-4">Table of Contents</h3>
      <nav>
        <ul className="space-y-2">
          {toc.map((item, index) => (
            <li key={index} className={`${item.level === 2 ? 'ml-0' : 'ml-4'}`}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className="text-gray-400 hover:text-white transition-colors text-sm block"
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

// Helper function to remove common indentation from markdown content
function dedent(str) {
  const lines = str.split('\n');
  // Remove any leading or trailing empty lines
  while (lines.length && lines[0].trim() === '') {
    lines.shift();
  }
  while (lines.length && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }
  // Find minimum indentation among all lines
  const indentLengths = lines.filter(line => line.trim()).map(line => {
    const match = line.match(/^(\s*)/);
    return match ? match[1].length : 0;
  });
  const minIndent = Math.min(...indentLengths);
  // Remove the common indentation from each line
  return lines.map(line => line.substring(minIndent)).join('\n');
}

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Helper function to validate business email
  const isBusinessEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
      'gmx.com', 'live.com', 'msn.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!isBusinessEmail(email)) {
      setError('Please use your business email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.subscribeToNewsletter(email);
      setSuccess(true);
      setEmail('');
    } catch (error) {
      setError(error.message || 'Failed to subscribe');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
      <div className="flex-1 relative">
        <input
          type="email"
          placeholder="Enter your business email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-6 py-3 bg-white/10 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF56A5]"
          required
          disabled={isSubmitting}
        />
      </div>
      <button
        type="submit"
        className="px-8 py-3 bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] rounded-full text-white font-medium hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <LoadingSpinnerRing tone="onDark" className="!h-5 !w-5" />
        ) : (
          'Subscribe'
        )}
      </button>
      {error && (
        <div className="absolute -bottom-8 left-0 right-0 text-red-500 text-sm text-center">
          {error}
        </div>
      )}
      {success && (
        <div className="absolute -bottom-8 left-0 right-0 text-green-500 text-sm text-center">
          Thanks for subscribing! Stay tuned—we'll be in touch soon!
        </div>
      )}
    </form>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const currentUrl = window.location.href;
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle initial content load
  useEffect(() => {
    if (post && !loading) {
      // Let the browser handle the initial hash navigation
      if (window.location.hash) {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          element.scrollIntoView();
        }
      }
    }
  }, [post, loading]);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const post = await api.getBlogPost(slug);
        setPost(post);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Failed to load blog post. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  // Format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="landing-page bg-background min-h-screen">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <AppLoadingState
            layout="compact"
            tone="marketing"
            label="Loading article…"
            className="min-h-[40vh]"
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="landing-page bg-background min-h-screen">
        <Helmet>
          <title>Article Not Found | Lifelike Agents Blog</title>
          <meta name="description" content="The article you're looking for could not be found." />
        </Helmet>
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
            <p className="text-gray-400 mb-8">{error || "The article you're looking for doesn't exist."}</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90"
            >
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Extract first paragraph for meta description
  const description = post.content
    .split('\n')
    .find(line => line.trim() && !line.startsWith('#'))
    .trim()
    .slice(0, 160);

  // Dedent the markdown content to remove unwanted indentation
  const markdownContent = dedent(post.content);

  return (
    <div className="landing-page bg-background min-h-screen">
      <Helmet>
        <title>{post.title} | Lifelike Agents Blog</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={post.image_url} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={post.image_url} />
        <meta name="article:published_time" content={new Date(post.date).toISOString()} />
        <meta name="article:section" content={post.category} />
      </Helmet>
      
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:flex-grow">
            {/* Article Header */}
            <div className="max-w-3xl mx-auto mb-12">
              <div className="flex items-center gap-4 mb-6">
                <Link
                  to="/blog"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Blog
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {post.category.split(',').map((category, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-300"
                  >
                    {category.trim()}
                  </span>
                ))}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-4 text-gray-400 mb-8">
                <span>{formatDate(post.date)}</span>
                <span>•</span>
                <span>{post.read_time} min read</span>
              </div>

              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-[400px] object-cover rounded-2xl mb-12"
              />
            </div>

            {/* Mobile Table of Contents */}
            <div className="max-w-3xl mx-auto">
              <TableOfContents content={post.content} isMobile={true} />
            </div>

            {/* Article Content */}
            <div className="max-w-3xl mx-auto">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8">
                <article className="markdown-content">
                  <ReactMarkdown
                    components={MarkdownComponents}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeSlug, rehypePrism]}
                  >
                    {markdownContent}
                  </ReactMarkdown>
                </article>
              </div>
            </div>

            {/* Share and Subscribe Section */}
            <div className="max-w-3xl mx-auto mt-12">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
                <div className="bg-gradient-to-br from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20 rounded-2xl p-8">
                  <div className="text-center">
                    <h2 className="text-2xl font-semibold text-white mb-4">
                      Share this article
                    </h2>
                    <div className="flex justify-center gap-4 mb-8">
                      <ShareButton
                        platform="twitter"
                        icon={
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                          </svg>
                        }
                        shareUrl={currentUrl}
                        title={post.title}
                      />
                      <ShareButton
                        platform="facebook"
                        icon={
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        }
                        shareUrl={currentUrl}
                        title={post.title}
                      />
                      <ShareButton
                        platform="linkedin"
                        icon={
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        }
                        shareUrl={currentUrl}
                        title={post.title}
                      />
                    </div>
                    
                    <div className="border-t border-white/10 pt-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">
                        Subscribe to Our Newsletter
                      </h2>
                      <p className="text-gray-300 mb-6">
                        Get the latest insights and updates delivered directly to your inbox.
                      </p>
                      <NewsletterForm />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <TableOfContents content={post.content} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPost;